import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { NgClass } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog } from '@angular/material/dialog';
import {
  EventService,
  ApiEvent,
  EventResponse,
  eventResponseOf,
} from '../../../../services/event.service';
import { EventDetail } from '../../../common/event-detail/event-detail';

@Component({
  selector: 'app-upcoming-deadlines',
  imports: [NgClass, RouterLink, MatCardModule, MatButtonModule, MatDividerModule],
  templateUrl: './upcoming-deadlines.html',
  styleUrl: './upcoming-deadlines.css',
})
export class UpcomingDeadlines implements OnInit {
  private eventService = inject(EventService);
  private dialog = inject(MatDialog);

  events = signal<ApiEvent[]>([]);
  showAll = signal(false);

  activeEvents = computed(() => {
    const now = Date.now();
    return this.events().filter((e) => {
      const timeStr = e.end_time ?? e.start_time;
      const [h, m] = timeStr.split(':').map(Number);
      const dt = new Date(e.day + 'T00:00:00');
      dt.setHours(h, m, 0, 0);
      return dt.getTime() > now;
    });
  });

  visibleEvents = computed(() =>
    this.showAll() ? this.activeEvents() : this.activeEvents().slice(0, 3),
  );

  openDetail(event: ApiEvent): void {
    this.dialog.open(EventDetail, {
      width: '480px',
      maxWidth: '95vw',
      data: { event },
    });
  }

  toggleShowAll(): void {
    this.showAll.update((v) => !v);
  }

  ngOnInit(): void {
    this.eventService.getSharedEvents().subscribe({
      next: (events) => this.events.set(events),
      error: (err) => console.error('getSharedEvents failed:', err),
    });
  }

  isAttending(event: ApiEvent): boolean {
    return eventResponseOf(event) === 'confirmed';
  }

  isDeclined(event: ApiEvent): boolean {
    return eventResponseOf(event) === 'declined';
  }

  respond(event: ApiEvent, status: EventResponse): void {
    if (eventResponseOf(event) === status) return;
    this.eventService.setEventResponse(event.id, status).subscribe(() => {
      this.eventService.getSharedEvents().subscribe((events) => this.events.set(events));
    });
  }

  private eventDateTime(event: ApiEvent): Date {
    const [h, m] = event.start_time.split(':').map(Number);
    const dt = new Date(event.day + 'T00:00:00');
    dt.setHours(h, m, 0, 0);
    return dt;
  }

  dateLabel(event: ApiEvent): string {
    const dt = this.eventDateTime(event);
    const monthDay = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const ms = dt.getTime() - Date.now();

    if (ms <= 0) return monthDay;

    const mins = Math.floor(ms / 60000);
    const hours = Math.floor(ms / 3600000);
    const days = Math.floor(ms / 86400000);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);

    if (mins < 60) return `${monthDay} · ${mins} ${mins === 1 ? 'minute' : 'minutes'}`;
    if (hours < 24) return `${monthDay} · ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    if (days < 7) return `${monthDay} · ${days} ${days === 1 ? 'day' : 'days'}`;
    if (weeks < 4) return `${monthDay} · ${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
    return `${monthDay} · ${months} ${months === 1 ? 'month' : 'months'}`;
  }

  badgeClass(event: ApiEvent): string {
    const ms = this.eventDateTime(event).getTime() - Date.now();
    if (ms <= 0) return 'info';
    const hours = ms / 3600000;
    if (hours < 24) return 'urgent';
    if (hours < 24 * 7) return 'upcoming';
    return 'info';
  }

  timeLabel(event: ApiEvent): string {
    const [h, m] = event.start_time.split(':').map(Number);
    const [eh, em] = event.end_time?.split(':').map(Number) || [0, 0];
    const start_period = h >= 12 ? 'PM' : 'AM';
    const end_period = eh >= 12 ? 'PM' : 'AM';
    const start_hour = h % 12 || 12;
    const end_hour = eh % 12 || 12;
    const start_min = m.toString().padStart(2, '0');
    const end_min = em.toString().padStart(2, '0');
    return eh === 0 && em === 0
      ? `${start_hour}:${start_min} ${start_period}`
      : `${start_hour}:${start_min} ${start_period}-${end_hour}:${end_min} ${end_period}`;
  }
}
